import React, { useMemo } from 'react';

import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, IconButton, List, ListItemButton, ListItemText, Stack, Typography } from '@mui/material';

import { ChatConversation, GENERAL_CONVERSATION_ID } from './AiChatContext';

interface AiChatConversationListProps {
  conversations: Record<string, ChatConversation>;
  activeConversationId: string | null;
  currentUrlCustomerId: string | null;
  onSelect: (conversationId: string) => void;
  onDelete: (conversationId: string) => void;
  onNewChat: () => void;
}

function getConversationLabel(conversation: ChatConversation): string {
  if (conversation.id === GENERAL_CONVERSATION_ID) {
    return 'General';
  }
  return conversation.customerName || conversation.customerId || 'Unknown';
}

function getLastMessagePreview(conversation: ChatConversation): string {
  const userMessages = conversation.messages.filter((m) => m.role !== 'system');
  if (userMessages.length === 0) {
    return 'No messages yet';
  }
  const last = userMessages[userMessages.length - 1];
  const preview = last.content.slice(0, 60);
  return preview.length < last.content.length ? `${preview}...` : preview;
}

function formatRelativeTime(timestamp: number): string {
  const diffMs = Date.now() - timestamp;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) {
    return 'just now';
  }
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

export default function AiChatConversationList({
  conversations,
  activeConversationId,
  currentUrlCustomerId,
  onSelect,
  onDelete,
  onNewChat,
}: AiChatConversationListProps) {
  const sortedConversations = useMemo(
    () => Object.values(conversations).sort((a, b) => b.lastActivityAt - a.lastActivityAt),
    [conversations],
  );

  return (
    <Box sx={{ flex: 1, overflow: 'auto' }}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ px: 2, py: 0.5 }}>
        <IconButton size="small" onClick={onNewChat} title="New chat for current page">
          <AddCommentOutlinedIcon fontSize="small" />
        </IconButton>
      </Stack>

      {sortedConversations.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1, display: 'block' }}>
          No conversations yet. Click the + to start one.
        </Typography>
      )}

      <List dense disablePadding>
        {sortedConversations.map((conv) => {
          const isActive = conv.id === activeConversationId;
          const isCurrentPageCustomer = currentUrlCustomerId != null && conv.customerId === currentUrlCustomerId;

          return (
            <ListItemButton
              key={conv.id}
              selected={isActive}
              onClick={() => onSelect(conv.id)}
              sx={{
                py: 0.5,
                borderLeft: isCurrentPageCustomer ? 3 : 0,
                borderColor: 'primary.main',
              }}
            >
              <ListItemText
                primary={getConversationLabel(conv)}
                secondary={
                  <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, mr: 1 }}>
                      {getLastMessagePreview(conv)}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
                      {formatRelativeTime(conv.lastActivityAt)}
                    </Typography>
                  </Box>
                }
                slotProps={{
                  primary: {
                    variant: 'body2',
                    fontWeight: isActive ? 'bold' : 'normal',
                    noWrap: true,
                  },
                  secondary: { component: 'div' },
                }}
              />
              <IconButton
                edge="end"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(conv.id);
                }}
                title="Delete conversation"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );
}
